import React, { useEffect, useRef, useState } from 'react';
import { X, Camera, RefreshCw, AlertCircle, Sparkles } from 'lucide-react';

interface QrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onScanSuccess: (roomId: string, accessCode: string) => void;
}

export const QrScannerModal: React.FC<QrScannerModalProps> = ({
  isOpen,
  onClose,
  onScanSuccess
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasCameraError, setHasCameraError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen]);

  const startCamera = async () => {
    setHasCameraError(false);
    setErrorMessage('');
    setIsScanning(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.play();
        scanFrames();
      }
    } catch (err: any) {
      console.error('Camera access error:', err);
      setHasCameraError(true);
      setErrorMessage(
        err.name === 'NotAllowedError'
          ? 'Camera permission denied. Please enable camera access in your settings.'
          : 'Unable to access rear camera on this device.'
      );
      setIsScanning(false);
    }
  };

  const stopCamera = () => {
    setIsScanning(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const scanFrames = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Check if BarcodeDetector is available natively (Android Chrome / WebView standard)
    if ('BarcodeDetector' in window) {
      const barcodeDetector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });

      const detectLoop = async () => {
        if (!isScanning && !isOpen) return;

        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          try {
            const barcodes = await barcodeDetector.detect(video);
            if (barcodes && barcodes.length > 0) {
              const rawVal = barcodes[0].rawValue;
              if (handleParsedUrl(rawVal)) {
                return;
              }
            }
          } catch {}
        }
        animationFrameRef.current = requestAnimationFrame(detectLoop);
      };
      detectLoop();
    } else {
      // Fallback frame check
      const checkLoop = () => {
        if (!isScanning && !isOpen) return;
        animationFrameRef.current = requestAnimationFrame(checkLoop);
      };
      checkLoop();
    }
  };

  const handleParsedUrl = (urlStr: string): boolean => {
    try {
      const url = new URL(urlStr);
      const room = url.searchParams.get('room');
      const key = url.searchParams.get('key');
      if (room && key) {
        stopCamera();
        onScanSuccess(room.toUpperCase(), key);
        onClose();
        return true;
      }
    } catch {
      // Direct raw format (e.g. ROOM:KEY)
      if (urlStr.includes(':')) {
        const parts = urlStr.split(':');
        if (parts.length === 2 && parts[0].startsWith('CK-')) {
          stopCamera();
          onScanSuccess(parts[0].toUpperCase(), parts[1]);
          onClose();
          return true;
        }
      }
    }
    return false;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl animate-in fade-in duration-200">
      <div className="glass-card w-full max-w-sm p-5 shadow-2xl border border-cyan-500/40 relative flex flex-col items-center text-center">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
        >
          <X size={18} />
        </button>

        <div className="w-12 h-12 rounded-2xl bg-cyan-950/60 border border-cyan-500/40 flex items-center justify-center text-cyan-400 mb-3 shadow-glow-cyan">
          <Camera size={22} className="animate-pulse" />
        </div>

        <h3 className="text-lg font-bold text-white font-display mb-1">Scan PC Pairing QR</h3>
        <p className="text-xs text-slate-400 mb-4 max-w-xs">
          Point your phone camera at the QR code displayed on your PC screen.
        </p>

        {/* Video Viewport with Target Reticle */}
        <div className="relative w-64 h-64 rounded-2xl overflow-hidden bg-black border-2 border-cyan-500/50 shadow-2xl flex items-center justify-center">
          <video ref={videoRef} className="w-full h-full object-cover" />
          <canvas ref={canvasRef} className="hidden" />

          {/* Scanner Reticle UI */}
          <div className="absolute inset-4 border border-cyan-400/40 rounded-xl pointer-events-none">
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-cyan-400"></div>
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-cyan-400"></div>
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-cyan-400"></div>
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-cyan-400"></div>

            {/* Scanning Laser Beam */}
            <div className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent shadow-[0_0_8px_#00F0FF] animate-bounce top-1/2 -translate-y-1/2"></div>
          </div>

          {hasCameraError && (
            <div className="absolute inset-0 bg-black/90 p-4 flex flex-col items-center justify-center gap-2 text-center">
              <AlertCircle size={24} className="text-rose-400" />
              <p className="text-xs text-slate-300">{errorMessage}</p>
              <button onClick={startCamera} className="btn-secondary text-xs py-1.5 px-3 mt-2 flex items-center gap-1">
                <RefreshCw size={12} />
                <span>Retry Camera</span>
              </button>
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-500 mt-4 font-mono">
          Detects room credentials and connects automatically
        </p>
      </div>
    </div>
  );
};
